import { forEach, has, isArray } from "lodash";
import type { FieldPath, FieldValues, UseFormSetError } from "react-hook-form";
import { ApiError } from "./reactQueryApi";

// Maps the Mongoid-shaped 422 payload (`{ errors: { field: ["msg", …] } }`)
// onto react-hook-form's `setError` so server-validated cases light up the
// field itself instead of only a generic toast. Keys without a form field —
// `base`, or anything not listed in `mappableFields` when given — fall back
// to a toast with the server's own message. Returns false when the error is
// no mappable 422 (callers show their generic failure toast then).
export function applyServerErrors<T extends FieldValues>(
  error: unknown,
  setError: UseFormSetError<T>,
  { mappableFields }: { mappableFields?: string[] } = {},
): boolean {
  if (!(error instanceof ApiError) || error.status !== 422) return false;
  const errors = error.responseJson?.errors as
    | Record<string, string[] | string>
    | undefined;
  if (!errors) return false;

  let handled = false;
  Object.entries(errors).forEach(([field, messages]) => {
    const message = Array.isArray(messages) ? messages[0] : messages;
    if (!message) return;

    if (
      field === "base" ||
      (mappableFields && !mappableFields.includes(field))
    ) {
      toastr.error(message);
    } else {
      setError(field as FieldPath<T>, { type: "server", message });
    }
    handled = true;
  });
  return handled;
}

function addErrorClass(errors) {
  forEach(errors, (error) => {
    if (isArray(error) || !has(error, "ref")) {
      addErrorClass(error);
    } else {
      error?.ref?.classList?.add("field-error");
      toastr.error(error?.message || I18n.t("generic_error"));
    }
  });
}

export function onValidationError(errors) {
  resetValidationErrors();
  addErrorClass(errors);
}

export function resetValidationErrors() {
  // spread first: the HTMLCollection is live, removing the class while
  // index-iterating would skip every other element
  forEach([...document.getElementsByClassName("field-error")], (element) => {
    // render-driven markings (field-error-live) clear themselves on
    // revalidation — removing them here would desync from React: the
    // unchanged className never gets re-written, so a still-invalid field
    // would lose its border on the second failed submit
    if (element?.classList.contains("field-error-live")) return;
    element?.classList.remove("field-error");
  });
}
